import { z } from 'zod';

import type {
  AddScenarioCommand,
  EvaluationProtocolElement,
  EvaluationProtocolElementDto,
  ScenarioPersona,
  UpdateScenarioSettingsCommand
} from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettings/hooks/useScenarioForm.ts';

const updateScenarioSettingsCommandSchema = z
  .object({
    notesEnabled: z.boolean(),
    notesEvaluationProtocolElements: z.array(z.custom<EvaluationProtocolElementDto>()).nullable().optional()
  })
  .passthrough();

const maybeValidateUpdateScenarioSettingsCommand = (command: UpdateScenarioSettingsCommand) => {
  if (process.env.NODE_ENV !== 'production') {
    updateScenarioSettingsCommandSchema.parse(command);
  }

  return command;
};

export const mapEvaluationProtocolElements = (
  elements?: EvaluationProtocolElementDto[] | null
): EvaluationProtocolElement[] | null => {
  if (!elements) {
    return null;
  }

  return elements.map((element) => ({
    ...element,
    description: element.description ?? null
  }));
};

export const mapScenarioPersonas = (
  scenarioPersonas?: ScenarioSettingsType['scenarioPersonas']
): ScenarioPersona[] =>
  scenarioPersonas
    ? scenarioPersonas.map((persona) => ({
        id: persona.id,
        scenarioId: persona.scenarioId,
        personaId: persona.personaId ?? persona.persona?.id,
        roleId: persona.roleId ?? persona.role?.id ?? undefined,
        personaUsages: persona.personaUsages ?? undefined
      }))
    : [];

const mapScenarioFormBase = (
  form: ScenarioSettingsType,
  currentCompanyId?: number | null
) => ({
  companyId: form.createSharedScenario ? null : form.companyId ?? currentCompanyId ?? null,
  name: form.name,
  description: form.description,
  speed: form.speed,
  langCode: form.langCode,
  evaluationProtocolElements: mapEvaluationProtocolElements(form.evaluationProtocolElements),
  notesEvaluationProtocolElements: mapEvaluationProtocolElements(form.notesEvaluationProtocolElements),
  scenarioPersonas: mapScenarioPersonas(form.scenarioPersonas),
  scenarioContextIntro: form.scenarioContextIntro ?? null,
  playScenarioContextOn: !!form.playScenarioContextOn,
  context: form.context ?? null,
  isLlmEnabled: form.isLlmEnabled,
  isDecisionSupportEnabled: form.isDecisionSupportEnabled,
  notesEnabled: form.notesEnabled,
  disableEndDetection: form.disableEndDetection,
  llm: form.llm,
  useStreaming: form.useStreaming,
  maxTokens: form.maxTokens,
  variables: form.variables,
  isPublished: form.isPublished
});

export const mapScenarioFormToUpdateCommand = (
  form: ScenarioSettingsType,
  options?: { scenarioId?: number; currentCompanyId?: number | null }
): UpdateScenarioSettingsCommand => {
  const command: UpdateScenarioSettingsCommand = {
    ...mapScenarioFormBase(form, options?.currentCompanyId),
    // For updates, preserve current scenario ownership unless explicitly set by form.
    // This prevents shared scenarios (companyId=null) from being remapped to current company.
    companyId: form.createSharedScenario ? null : form.companyId ?? null,
    id: options?.scenarioId ?? form.id
  };

  return maybeValidateUpdateScenarioSettingsCommand(command);
};

export const mapScenarioFormToAddCommand = (
  form: ScenarioSettingsType,
  options: { currentCompanyId?: number | null }
): AddScenarioCommand => {
  const base = mapScenarioFormBase(form, options.currentCompanyId);

  return base;
};
